import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import type { CheckoutIo } from '../model/checkout_env.js';
import type {
  ConciergeBedType,
  ConciergeBusSeatClass,
  ConciergeBusSeatPosition,
  ConciergeBusSeatRowPosition,
  ConciergeFlightPeriod,
  ConciergeProductType,
  ConciergeReasonOption,
  ConciergeRentalOption,
  ConciergeRentalOptions,
} from '../model/checkout_source.js';

/** Fase do catálogo de motivos do pedido. `absent`: o host não injetou a
 *  leitura (ou ela falhou) — o campo some e o pedido segue sem motivo. */
type ConciergeReasonsPhase = 'loading' | 'ready' | 'absent';

/** Fase dos catálogos do pedido de carro. `failed` é fail-closed: sem
 *  categoria não há pedido (o fornecedor recusa), então o cartão oferece
 *  tentar de novo em vez de seguir sem ela. Fora do carro fica `idle`. */
type ConciergeRentalOptionsPhase = 'idle' | 'loading' | 'ready' | 'failed';

/** A categoria do catálogo que casa com a dica da busca (código da faceta
 *  `SUV` × id do catálogo `suv`: só a caixa difere). */
function hintedCategory(
  categories: readonly ConciergeRentalOption[],
  hint: string | undefined,
): string {
  if (!hint) return '';
  const wanted = hint.toLowerCase();
  return categories.find((category) => category.id.toLowerCase() === wanted)?.id ?? '';
}

/** A chave de UM motivo do catálogo na seleção. `id` sozinho não serve: o
 *  concierge-api devolve os documentos da coleção `reason` como estão, e em
 *  develop os cinco motivos de voo compartilham o mesmo `id` — só o `name` os
 *  separa (é o que o flight-business grava no voucher). */
export function conciergeReasonKey(reason: Pick<ConciergeReasonOption, 'id' | 'name'>): string {
  return `${reason.id}\u001f${reason.name}`;
}

export interface ConciergeRequestState {
  reasonsPhase: ConciergeReasonsPhase;
  reasons: ConciergeReasonOption[];
  /** `conciergeReasonKey` do motivo escolhido; vazio = nenhum. */
  reasonKey: string;
  details: string;
  // Só voo.
  onlyDirect: boolean;
  onlyWithLuggage: boolean;
  /** Um período por trecho (voo) ou por perna (ônibus, ida e volta), na ordem
   *  do pedido; `undefined` = indiferente no voo, ainda não escolhido no ônibus. */
  periods: (ConciergeFlightPeriod | undefined)[];
  // Só hotel.
  preferredHotel: string;
  bedType: ConciergeBedType | undefined;
  // Só ônibus — obrigatórios no pedido, como no app.
  busSeat: ConciergeBusSeat;
  // Só carro.
  rentalOptionsPhase: ConciergeRentalOptionsPhase;
  rentalCategories: ConciergeRentalOption[];
  rentalCompanies: ConciergeRentalOption[];
  /** Id da categoria escolhida; vazio = nenhuma (obrigatória). */
  categoryId: string;
  /** Id (code) da locadora; vazio = indiferente. */
  companyId: string;
}

/** Onde sentar no ônibus; um campo ausente ainda não foi escolhido. */
interface ConciergeBusSeat {
  position?: ConciergeBusSeatPosition;
  rowPosition?: ConciergeBusSeatRowPosition;
  seatClass?: ConciergeBusSeatClass;
}

type Action =
  | { type: 'REASONS_LOADING' }
  | { type: 'REASONS_OK'; reasons: ConciergeReasonOption[] }
  | { type: 'REASONS_ABSENT' }
  | { type: 'SET_REASON'; value: string }
  | { type: 'SET_DETAILS'; value: string }
  | { type: 'SET_ONLY_DIRECT'; value: boolean }
  | { type: 'SET_ONLY_WITH_LUGGAGE'; value: boolean }
  | { type: 'SET_PERIOD'; index: number; value: ConciergeFlightPeriod | undefined }
  | { type: 'SET_PREFERRED_HOTEL'; value: string }
  | { type: 'SET_BED_TYPE'; value: ConciergeBedType | undefined }
  | { type: 'SET_BUS_SEAT'; patch: ConciergeBusSeat }
  | { type: 'RENTAL'; action: RentalAction };

/** As ações do cartão do carro, num reducer à parte. */
type RentalAction =
  | { type: 'OPTIONS_LOADING' }
  | {
      type: 'OPTIONS_OK';
      options: ConciergeRentalOptions;
      categoryHint: string | undefined;
      /** Releitura por "tentar de novo": a escolha anterior da pessoa vence a
       *  dica. Fora dela (uma dica nova chegou) a dica é que vale. */
      retry: boolean;
    }
  | { type: 'OPTIONS_FAILED' }
  | { type: 'SET_CATEGORY'; value: string }
  | { type: 'SET_COMPANY'; value: string };

function rentalReducer(state: ConciergeRequestState, action: RentalAction): ConciergeRequestState {
  switch (action.type) {
    case 'OPTIONS_LOADING':
      return { ...state, rentalOptionsPhase: 'loading' };
    case 'OPTIONS_OK': {
      const { categories, companies } = action.options;
      const keep = action.retry && categories.some((category) => category.id === state.categoryId);
      return {
        ...state,
        rentalOptionsPhase: 'ready',
        rentalCategories: categories,
        rentalCompanies: companies,
        categoryId: keep ? state.categoryId : hintedCategory(categories, action.categoryHint),
        companyId:
          action.retry && companies.some((company) => company.id === state.companyId)
            ? state.companyId
            : '',
      };
    }
    case 'OPTIONS_FAILED':
      return { ...state, rentalOptionsPhase: 'failed' };
    case 'SET_CATEGORY':
      return { ...state, categoryId: action.value };
    case 'SET_COMPANY':
      return { ...state, companyId: action.value };
    default: {
      const exhaustive: never = action;
      return exhaustive;
    }
  }
}

function reducer(state: ConciergeRequestState, action: Action): ConciergeRequestState {
  switch (action.type) {
    case 'REASONS_LOADING':
      // Catálogo de OUTRO modal a caminho: a escolha anterior não vale mais e
      // `canSubmit` fecha até a nova lista chegar.
      return { ...state, reasonsPhase: 'loading', reasons: [], reasonKey: '' };
    case 'REASONS_OK': {
      // O default do catálogo já vem marcado (no máximo um por modal); uma
      // escolha anterior da pessoa vence o default se ainda existir na lista.
      const keep = action.reasons.some((reason) => conciergeReasonKey(reason) === state.reasonKey);
      const fallback = action.reasons.find((reason) => reason.isDefault);
      return {
        ...state,
        reasonsPhase: 'ready',
        reasons: action.reasons,
        reasonKey: keep ? state.reasonKey : fallback ? conciergeReasonKey(fallback) : '',
      };
    }
    case 'REASONS_ABSENT':
      return { ...state, reasonsPhase: 'absent', reasons: [], reasonKey: '' };
    case 'SET_REASON':
      return { ...state, reasonKey: action.value };
    case 'SET_DETAILS':
      return { ...state, details: action.value };
    case 'SET_ONLY_DIRECT':
      return { ...state, onlyDirect: action.value };
    case 'SET_ONLY_WITH_LUGGAGE':
      return { ...state, onlyWithLuggage: action.value };
    case 'SET_PERIOD':
      return {
        ...state,
        periods: state.periods.map((period, i) => (i === action.index ? action.value : period)),
      };
    case 'SET_PREFERRED_HOTEL':
      return { ...state, preferredHotel: action.value };
    case 'SET_BED_TYPE':
      return { ...state, bedType: action.value };
    case 'SET_BUS_SEAT':
      return { ...state, busSeat: { ...state.busSeat, ...action.patch } };
    case 'RENTAL':
      return rentalReducer(state, action.action);
    default: {
      const exhaustive: never = action;
      return exhaustive;
    }
  }
}

/** Cada perna com período e o assento escolhido — o que o app exige do ônibus. */
function busAsksComplete({ periods, busSeat }: ConciergeRequestState): boolean {
  return (
    periods.every((period) => period !== undefined) &&
    busSeat.position !== undefined &&
    busSeat.rowPosition !== undefined &&
    busSeat.seatClass !== undefined
  );
}

export interface UseConciergeRequestOptions {
  /** The host's reads; without them the reason field goes away and the rental
   *  request cannot close. */
  io: CheckoutIo | null;
  productType: ConciergeProductType;
  /** Quantos trechos (voo) ou pernas (ônibus) o pedido tem — um período por um. */
  segmentCount: number;
  /** Restrições que a busca já trazia ligadas (só direto) — entram marcadas. */
  initialOnlyDirect?: boolean;
  /** Carro: a categoria do filtro da busca, que entra marcada. */
  categoryHint?: string | undefined;
}

export interface UseConciergeRequestResult extends ConciergeRequestState {
  /** O que SÓ este formulário exige: motivo do pedido quando há catálogo, a
   *  observação no hotel, no ônibus e no carro (o briefing que a equipe usa para
   *  cotar — obrigatória no legado e, no carro, no fornecedor), o período de cada
   *  perna e onde sentar no ônibus, e a categoria do carro.
   *  O restante (rateio, motivo corporativo) é do form multi. */
  canSubmit: boolean;
  setReasonKey: (value: string) => void;
  setDetails: (value: string) => void;
  setOnlyDirect: (value: boolean) => void;
  setOnlyWithLuggage: (value: boolean) => void;
  setPeriod: (index: number, value: ConciergeFlightPeriod | undefined) => void;
  setPreferredHotel: (value: string) => void;
  setBedType: (value: ConciergeBedType | undefined) => void;
  setBusSeat: (patch: ConciergeBusSeat) => void;
  setCategoryId: (value: string) => void;
  setCompanyId: (value: string) => void;
  /** Carro: relê os catálogos depois de uma falha. */
  retryRentalOptions: () => void;
}

/** O estado do CARTÃO "Pedido à equipe VOLL" — o que a pessoa acrescenta ao
 *  perfil herdado. Carrega o catálogo de motivos pelo port do checkout
 *  (fail-soft: sem port ou com a leitura falhando o campo some). */
export function useConciergeRequest({
  io: port,
  productType,
  segmentCount,
  initialOnlyDirect = false,
  categoryHint,
}: UseConciergeRequestOptions): UseConciergeRequestResult {
  const [state, dispatch] = useReducer(reducer, undefined, () => ({
    reasonsPhase: 'loading' as const,
    reasons: [],
    reasonKey: '',
    details: '',
    onlyDirect: initialOnlyDirect,
    onlyWithLuggage: false,
    periods: Array.from({ length: segmentCount }, () => undefined),
    preferredHotel: '',
    bedType: undefined,
    busSeat: {},
    rentalOptionsPhase: productType === 'rental' ? ('loading' as const) : ('idle' as const),
    rentalCategories: [],
    rentalCompanies: [],
    categoryId: '',
    companyId: '',
  }));
  const read = port?.getConciergeReasons;
  const readRentalOptions = port?.getRentalConciergeOptions;
  const [rentalAttempt, retry] = useReducer((attempt: number) => attempt + 1, 0);
  const loadedAttempt = useRef(rentalAttempt);

  useEffect(() => {
    // O carro não tem catálogo de motivo do pedido (`ConciergeReasonProductType`).
    if (!read || productType === 'rental') {
      dispatch({ type: 'REASONS_ABSENT' });
      return undefined;
    }
    let cancelled = false;
    dispatch({ type: 'REASONS_LOADING' });
    read(productType)
      .then((reasons) => {
        if (cancelled) return;
        if (reasons.length === 0) dispatch({ type: 'REASONS_ABSENT' });
        else dispatch({ type: 'REASONS_OK', reasons });
      })
      .catch(() => {
        if (!cancelled) dispatch({ type: 'REASONS_ABSENT' });
      });
    return () => {
      cancelled = true;
    };
  }, [read, productType]);

  useEffect(() => {
    if (productType !== 'rental') return undefined;
    if (!readRentalOptions) {
      dispatch({ type: 'RENTAL', action: { type: 'OPTIONS_FAILED' } });
      return undefined;
    }
    let cancelled = false;
    const isRetry = loadedAttempt.current !== rentalAttempt;
    loadedAttempt.current = rentalAttempt;
    dispatch({ type: 'RENTAL', action: { type: 'OPTIONS_LOADING' } });
    readRentalOptions()
      .then((options) => {
        if (cancelled) return;
        if (options.categories.length === 0)
          dispatch({ type: 'RENTAL', action: { type: 'OPTIONS_FAILED' } });
        else
          dispatch({
            type: 'RENTAL',
            action: { type: 'OPTIONS_OK', options, categoryHint, retry: isRetry },
          });
      })
      .catch(() => {
        if (!cancelled) dispatch({ type: 'RENTAL', action: { type: 'OPTIONS_FAILED' } });
      });
    return () => {
      cancelled = true;
    };
  }, [readRentalOptions, productType, categoryHint, rentalAttempt]);

  const canSubmit = useMemo(() => {
    if (state.reasonsPhase === 'loading') return false;
    if (state.reasonsPhase === 'ready' && !state.reasonKey) return false;
    if (productType === 'flight') return true;
    if (state.details.trim() === '') return false;
    // O catálogo carregado e a categoria nele: com a releitura falhada o cartão
    // esconde as categorias, e o botão não envia o que a pessoa não vê.
    if (productType === 'rental') {
      return (
        state.rentalOptionsPhase === 'ready' &&
        state.rentalCategories.some((category) => category.id === state.categoryId)
      );
    }
    return productType === 'hotel' || busAsksComplete(state);
  }, [state, productType]);

  return {
    ...state,
    canSubmit,
    setReasonKey: useCallback((value: string) => {
      dispatch({ type: 'SET_REASON', value });
    }, []),
    setDetails: useCallback((value: string) => {
      dispatch({ type: 'SET_DETAILS', value });
    }, []),
    setOnlyDirect: useCallback((value: boolean) => {
      dispatch({ type: 'SET_ONLY_DIRECT', value });
    }, []),
    setOnlyWithLuggage: useCallback((value: boolean) => {
      dispatch({ type: 'SET_ONLY_WITH_LUGGAGE', value });
    }, []),
    setPeriod: useCallback((index: number, value: ConciergeFlightPeriod | undefined) => {
      dispatch({ type: 'SET_PERIOD', index, value });
    }, []),
    setPreferredHotel: useCallback((value: string) => {
      dispatch({ type: 'SET_PREFERRED_HOTEL', value });
    }, []),
    setBedType: useCallback((value: ConciergeBedType | undefined) => {
      dispatch({ type: 'SET_BED_TYPE', value });
    }, []),
    setBusSeat: useCallback((patch: ConciergeBusSeat) => {
      dispatch({ type: 'SET_BUS_SEAT', patch });
    }, []),
    setCategoryId: useCallback((value: string) => {
      dispatch({ type: 'RENTAL', action: { type: 'SET_CATEGORY', value } });
    }, []),
    setCompanyId: useCallback((value: string) => {
      dispatch({ type: 'RENTAL', action: { type: 'SET_COMPANY', value } });
    }, []),
    retryRentalOptions: retry,
  };
}
